define('uikit/modules/token.input',['require','exports','module','underscore','marionette','backbone','../mixins/component.mixin','../channel','../collections/search.results','./token','../extra/key.codes','../helper/dom','templates'],function(require, exports, module) {
  //prettier-ignore
  var _ = require('underscore');
  var Mn = require('marionette');
  var Backbone = require('backbone');
  var ComponentMixin = require('../mixins/component.mixin');
  var channel = require('../channel');
  var SearchResultsView = require('../collections/search.results');
  var TokenView = require('./token');
  var KeyCodes = require('../extra/key.codes');
  var DOMHelper = require('../helper/dom');
  var Templates = require('templates');

  var Token = Backbone.Model.extend({
    defaults: {
      text: '',
      value: '',
      textModel: null,
      valueModel: null,
    },
  });

  var Tokens = Backbone.Collection.extend({ model: Token });

  var TokenInputView = Mn.CompositeView.extend(ComponentMixin).extend({
    className: 'ui visual search',

    childView: TokenView,

    childViewContainer: '.token.box',

    childViewEventPrefix: 'token',

    childViewOptions: function() {
      return {
        single: this.getBoolOption('singleValue'),
        wrappable: true,
        selectValueOnTab: this.getOption('selectValueOnTab'),
      };
    },

    template: Templates.App['token.input'],

    options: {
      createOnBlur: true,
      defaultText: 'Text',
      focusOnCreate: true,
      freeText: true,
      icon: '',
      placeholder: 'Click to search',
      separators: [KeyCodes.SEMICOLON_KEY, KeyCodes.COMMA], // ',' or ';'
      singleValue: true,
      validateToken: null,
      allowClearAll: false,
      selectValueOnTab: true,
      visible: true,
      errorMessageTimeout: 2000,
      defaultErrorMessage: 'Invalid Entry',
    },

    forceUpdate: {
      createOnBlur: true,
      defaultText: true,
      focusOnCreate: true,
      freeText: true,
      icon: true,
      placeholder: true,
      singleValue: true,
      validateToken: true,
      visible: true,
      allowClearAll: true,
      errorMessageTimeout: true,
    },

    childEvents: {
      'resultsView:result:selected': 'selectedSearchResult',
      'resultsView:result:active': 'changedActiveSearchResult',
    },

    events: {
      'blur @ui.input': 'handleInputBlur',
      'focus @ui.input': 'handleInputFocus',
      'keydown @ui.input': 'handleInputKeydown',
      'keyup @ui.input': 'handleInputKeyup',

      'click @ui.clearResults': 'handleClearClick',
      'click @ui.tokens .ui.label': 'handleTokensClick',
      'click .prompt': 'handlePromptClick',
    },

    ui: {
      input: '.token.input',
      tokens: '.token.box',
      clearResults: '.token.clear.icon',
      placeholder: '.placeholder',
    },

    initialize: function() {
      ComponentMixin.initialize.apply(this, arguments);

      this.collection = new Tokens();
      this.resultsView = new SearchResultsView();

      // Ignore blur on any touchstart or mousedown events on the results dropdown
      this.$el.on('touchstart mousedown', '.results .result', _.debounce(_.bind(this.ignoreBlur, this), 0, true));

      // Depending on whether there are tokens, hide the clear all icon
      this.listenTo(this.collection, 'add remove reset', this._toggleClearVisibility);
    },

    _toggleClearVisibility: function() {
      var length = this.collection.length;

      if (this.isRendered) {
        if (length) {
          this.ui.clearResults.show();
        } else {
          this.ui.clearResults.hide();
        }
      }
    },

    /**
     *  Define the values set in a new token model
     */
    defineNewTokenObject: function(useInputTextOnly) {
      var tokenObj = {},
        results = this.resultsView,
        selected = results.getSelected(), // currently selected result in the list
        textInput = this.ui.input.text();

      // If a search result is selected, cache the result model and set the text selected
      if (selected && !useInputTextOnly) {
        tokenObj.text = selected.text;
        tokenObj.textModel = selected.model;
      } else {
        // If you only allow single input tokens,
        // then automatically set the text field based on the input
        if (this.singleValue) {
          tokenObj.text = textInput;

          // set the value field and default the text field to the "default" value or 'Text'
          // ex. used for fuzzy search
        } else {
          tokenObj.text = this.getOption('defaultText');
          tokenObj.value = textInput;
        }
      }

      return tokenObj;
    },

    /**
     *  Create the token model, and insert into collection
     */
    generateNewToken: function(ignoreFocus, useInputTextOnly) {
      var token = new Token(this.defineNewTokenObject(useInputTextOnly)),
        tokenView = null,
        trimmedText = token.get('text').trim();

      token.set('text', trimmedText);

      // Insert new token into the collection
      this.collection.push(token);

      // Retrieve token view
      tokenView = this.children.findByModel(token);

      // If single selection, or token already has a value set (fuzzy search),
      // trigger new token created event
      if (this.singleValue || token.get('value')) {
        this._tokenCreated(token, ignoreFocus);
      } else {
        // On multi value tokens, set cursor to the details element of the token
        token._newToken = true;
        tokenView.setCursorToStart();
      }

      // Hide the results view
      this.hideResultsView();

      // Clear input field, after generating token
      this.ui.input.text('');
    },

    /**
     * Verify that a token is valid
     */
    isValidToken: function(token) {
      if (_.isFunction(this.getOption('validateToken'))) {
        try {
          var response = this.getOption('validateToken').call(this, token);
          if (_.isString(response)) {
            this.previousErrorMessage = response;
          } else {
            this.previousErrorMessage = null;
          }
          if (response !== true) {
            return false;
          }
        } catch (e) {
          if (_.isString(e.message)) {
            this.previousErrorMessage = e.message;
          } else {
            this.previousErrorMessage = null;
          }
          return false;
        }
      }
      return true;
    },

    focusCursorOnInput: function() {
      var input = this.ui.input.get(0);

      if (document.activeElement === input) {
        // if we are already focused then we don't need
        // to do anythign else
        return;
      }
      DOMHelper.placeCaretAtEnd(input);
    },

    /**
     *  Clear all tokens and show initial view
     */
    handleClearClick: function(ev) {
      var tokens = this.collection.models.slice(0),
        self = this;

      // Remove all tokens
      tokens.forEach(function(token) {
        self.collection.remove(token);
        self.triggerMethod('token:remove', token);
      });

      // Reset intial search view
      this.resetSearchView();

      // Don't allow click to proceed
      ev.stopPropagation();
    },

    handleTokensClick: function(ev) {
      ev.stopPropagation();
    },

    /**
     *  When user clicks on the visual search,
     *  focus curson on the token input,
     *  hide the placeholder,
     *  and trigger event for the search results
     */
    handlePromptClick: function(ev) {
      this.resetIgnoreFlag();

      ev.preventDefault();

      // Hide input placeholder
      this.hidePlaceholder();

      // Set focus on cursor
      this.focusCursorOnInput();

      // Initial trigger for search results
      this.triggerMethod('search:results:text', this.ui.input.text());
    },

    /**
     *  Anytime the token input is focused, hide the placeholder if visible
     */
    handleInputFocus: function() {
      this.hidePlaceholder();
      this.trigger('focus');
    },

    /**
     *  If blur initiated, hide search results and show placeholder
     *  unless flag set to ignore
     */
    handleInputBlur: function(ev) {
      if (!this._ignoreBlur) {
        var self = this;

        _.defer(function() {
          var textContent = self.ui.input.text().trim();
          if (self.createOnBlur && textContent.length > 0 && self.freeText) {
            // use the useInputTextOnly option because the user
            // did not specifically choose the selected item in
            // the results view
            self.generateNewToken(true, true);
          } else {
            // clear out the currently selected
            // input
            self.ui.input.text('');
          }
          self.resetSearchView();
        });
      }

      this.resetIgnoreFlag();
    },

    shouldCreateToken: function() {
      var textContent = this.ui.input.text();
      return (textContent && this.freeText) || (!this.isResultsEmpty() && this.resultsView.getSelected());
    },

    showErrorMessageOnPrompt: function() {
      var self = this;
      var message =
        this.previousErrorMessage == null ? this.getOption('defaultErrorMessage') : this.previousErrorMessage;

      if (!message) {
        return;
      }

      this.ui.input
        .popup('destroy')
        .popup({
          on: 'manual',
          position: 'top center',
          inline: true,
          content: message,
          variation: 'inverted',
        })
        .popup('show');

      _.defer(function() {
        // hide if the user types again
        self.once('keyup', function() {
          self.ui.input.popup('hide');
        });
      });

      if (this.errorMessageTimeout > -1) {
        setTimeout(function() {
          // hide after a timeout
          self.ui.input.popup('hide');
        }, this.errorMessageTimeout);
      }
    },

    resetSearchView: function() {
      this.hideResultsView();
      this.showPlaceholder();
    },

    ignoreBlur: function() {
      this._ignoreBlur = true;
    },

    resetIgnoreFlag: function() {
      this._ignoreBlur = false;
    },

    showPlaceholder: function() {
      if (!(this.collection && this.collection.length) && !this.ui.input.text().length) {
        this.ui.placeholder.show();
      }
    },

    hidePlaceholder: function() {
      this.ui.placeholder.hide();
    },

    changedActiveSearchResult: function(v, result) {
      // TODO - set on single value tokens as well?
      if (!this.singleValue) {
        if (this.selected) {
          // Set the result text
          if (result) {
            this.selected.setDetail(result.text);

            // If empty, reset the value
          } else {
            this.selected.resetDetail();
          }
        }
      }
    },

    selectedSearchResult: function(results, selected) {
      if (!this.selected) {
        this.generateNewToken();
      } else {
        // If editing multi value token
        if (!this.getOption('singleValue')) {
          // Set detail to the selected text
          this.selected.detail = selected.text;
        }

        this.selected.completeEdit();
      }
    },

    /**
     *  Return whether entered keycode is a token separator
     *  @param {Number} keyCode
     *  @returns {Boolean}
     */
    isKeyCodeTokenSeparator: function(keyCode) {
      var separators = this.getOption('separators') || [],
        separatorKeyCode = null;

      if (_.isArray(separators)) {
        for (var s = 0, numOfSeparators = separators.length; s < numOfSeparators; s++) {
          separatorKeyCode = separators[s];

          // Check whether valid keycode
          if (!_.isNumber(separatorKeyCode)) {
            continue;
          }

          // Return true if key pressed is a token separator
          if (separatorKeyCode == keyCode) {
            return true;
          }
        }
      }
      return false;
    },

    /**
     *  Handle keydown actions for the token input,
     *  Down and Up Arrow keys navigate in the search results,
     *  Enter and Tab keys generate a new token,
     *  Backspace key can focus on the last token in the list
     */
    handleInputKeydown: function(ev) {
      var tokens = this.children,
        keyCode = ev.keyCode,
        preInputTextContent = this.ui.input.text();

      // Cache initial text value
      this._oldTextContent = preInputTextContent;

      if (keyCode === KeyCodes.ENTER_KEY) {
        ev.preventDefault();

        // If tab key and there is text content, then prevent default behavior
      } else if (keyCode === KeyCodes.TAB_KEY && preInputTextContent) {
        ev.preventDefault();
      } else if (keyCode === KeyCodes.TAB_KEY) {
        DOMHelper.clearSelection();

        // If backspace key and there is text content, focus and select the last token
      } else if (keyCode === KeyCodes.BACKSPACE_KEY) {
        if (!preInputTextContent.length && tokens.length) {
          // Remove focus on input
          DOMHelper.clearSelection();
          this.resetSearchView();

          // Set focus on the last token
          tokens.findByIndex(tokens.length - 1).setFocus();

          ev.preventDefault();
        }
        // If down arrow key, select next sibling of the active result
      } else if (keyCode === KeyCodes.DOWNARROW_KEY) {
        ev.preventDefault();
        this.resultsView.selectNextResult();

        // If up arrow key, select previous sibling of active result
      } else if (keyCode === KeyCodes.UPARROW_KEY) {
        ev.preventDefault();
        this.resultsView.selectPreviousResult();

        // If keyCode is one of the token separators, then prevent default behavior
      } else if (this.isKeyCodeTokenSeparator(keyCode)) {
        ev.preventDefault();
      } else if (keyCode === KeyCodes.ESCAPE_KEY) {
        ev.preventDefault();
        this.resetSearchView();
      }
    },

    /**
     *  Handle keyup actions for the token input,
     *  Down and Up Arrow keys navigate in the search results,
     *  Enter and Tab keys generate a new token,
     *  Otherwise trigger the event for search results
     */
    handleInputKeyup: function(ev) {
      var keyCode = ev.keyCode,
        textContent = this.ui.input.text();

      // Prevent default behaviour of arrow keys
      if (keyCode === KeyCodes.DOWNARROW_KEY) {
        ev.preventDefault();
      } else if (keyCode === KeyCodes.UPARROW_KEY) {
        ev.preventDefault();

        // Generate a new token, if pressing enter
      } else if (
        keyCode === KeyCodes.ENTER_KEY ||
        keyCode === KeyCodes.TAB_KEY ||
        this.isKeyCodeTokenSeparator(keyCode)
      ) {
        ev.preventDefault();

        // Only if text entered, or a search result is selected
        if (this.shouldCreateToken()) {
          this.generateNewToken();
        } else {
          this.hideResultsView();
        }
        // Default to trigger new search results
      } else {
        // Firefox fix for random insertion of br
        if (keyCode == KeyCodes.BACKSPACE_KEY && !textContent.length) {
          this.ui.input.html('');
        }
        if (this._oldTextContent != textContent) {
          this.triggerMethod('search:results:text', textContent);
        }
        this._oldTextContent = '';
      }
      this.triggerMethod('keyup', ev);
    },

    hideResultsView: function() {
      this.resultsView.collection = null;
    },

    isResultsEmpty: function() {
      return !(this.resultsView.collection && this.resultsView.collection.length);
    },

    /**
     *  After rendering, show placeholder
     */
    onRender: function() {
      this._toggleClearVisibility();
      this.showPlaceholder();
    },

    onTokenKeypress: function(token, ev) {
      var keycode = ev.keyCode;

      // On Down arrow, select the next search result
      if (keycode == KeyCodes.DOWNARROW_KEY) {
        this.resultsView.selectNextResult();

        // On Up arrow, select the previous search result
      } else if (keycode == KeyCodes.UPARROW_KEY) {
        this.resultsView.selectPreviousResult();
      } else if (keycode == KeyCodes.ENTER_KEY || (this.getOption('selectValueOnTab') && keycode == KeyCodes.TAB_KEY)) {
        var selected = this.resultsView.getSelected();

        if (selected) {
          if (!this.getOption('singleValue')) {
            token.detail = selected.text;
          }
        }

        token.completeEdit();
      }

      // TODO - do we want anything to be done when pressing the left and right arrow key
      // such as selecting the next or previous token?
    },

    onTokenClose: function(v) {
      var self = this;

      this.selected = null;

      v.hide({
        animation: 'scale',
        onComplete: function() {
          self.collection.remove(v.model);
          self.triggerMethod('token:remove', v.model);

          self.focusCursorOnInput();
        },
      });
    },

    /**
     * Handles when a tokens value is being edited
     */
    onTokenValueEdit: function(v, value) {
      this.triggerMethod('search:results:value', value, v.text);
    },

    onTokenComplete: function(v) {
      // Assures that all selections have been cleared
      DOMHelper.clearSelection();

      // Trigger initial add event if new token
      if (v.model._newToken) {
        this._tokenCreated(v.model);

        v.model._newToken = false;
      } else {
        // Trigger edit event if existing token
        this._tokenRevised(v.model);
      }
    },

    onTokenEditing: function(v) {
      // TODO - allow for single value token to be possibly be edited
      var multiValue = !this.getOption('singleValue'),
        self = this;

      // Select the token being edited, if not already selected
      if (!v.selected) {
        this.selectToken(v);
      }

      // Sets the token to an edit state
      v.editing = true;

      // Triggger search results on the value for multi valued token
      if (multiValue) {
        _.defer(function() {
          self.triggerMethod('search:results:value', v.detail, v.text);
        });
      }
    },

    onTokenNotEditing: function(v) {
      v.editing = false;

      this.hideResultsView();
    },

    onTokenDeselect: function(v) {
      this.selected = null;

      v.selected = false;
    },

    onTokenSelected: function(v) {
      this.selectToken(v);
    },

    _tokenCreated: function(token, ignoreFocus) {
      if (this.focusOnCreate && !ignoreFocus) {
        DOMHelper.clearSelection();

        this.ui.input.focus();
      }

      if (!this.isValidToken(token)) {
        this.collection.remove(token);
        // need to show an error message here
        this.showErrorMessageOnPrompt();
        return;
      } else {
        this.triggerMethod('token:add', token);
      }
    },

    _tokenRevised: function(token) {
      if (!this.isValidToken(token)) {
        this.collection.remove(token);
        // need to show an error message here
        this.showErrorMessageOnPrompt();
        return;
      } else {
        this.triggerMethod('token:revised', token);
      }
    },

    selectToken: function(v) {
      this.selected = v;

      this.children.without(v).forEach(function(c) {
        c.selected = false;
        c.editing = false;
      });

      v.selected = true;
    },

    updateAttributes: function() {
      this.addClass('icon', this.getBoolOption('icon'));
    },

    /** Public Methods **/

    /**
     * Returns the collection of tokens
     * @return {Backbone.Collection}
     */
    getTokens: function() {
      return this.collection;
    },

    /**
     * Sets and overrides any existing tokens
     * @param {Collection} tokens
     */
    setTokens: function(tokens) {
      if (_.isArray(tokens)) {
        this.collection.reset(tokens);
      } else {
        this.collection.reset(tokens.models);
      }

      return this;
    },

    /**
     * Set a collection of results on the search results view
     * @param {Array||Collection} results   Available search results
     */
    setSearchResults: function(results) {
      // If results are an array, convert to a collection
      if (_.isArray(results)) {
        results = new Backbone.Collection(results);
      }

      // Set the new collection
      this.resultsView.collection = results;

      return this;
    },

    /**
     * Set the bindings for the search results view
     * @param {Object} bindings     Bindings for a single search result
     */
    setSearchResultBindings: function(bindings) {
      this.resultsView.childBindings = bindings;

      return this;
    },
  });

  TokenInputView.Token = Token;
  TokenInputView.Tokens = Tokens;

  // register the table component
  channel.reply('token-input', function() {
    return TokenInputView;
  });

  module.exports = TokenInputView;
});

